import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { ResponsiveMapLayout } from '@/components/homepage/ResponsiveMapLayout';
import { HeroSection } from '@/components/homepage/HeroSection';
import { PopularDestinations } from '@/components/homepage/PopularDestinations';
import { WhyChooseUs } from '@/components/homepage/WhyChooseUs';
import { Footer } from '@/components/homepage/Footer';
import { HomePageBusinessGrid } from '@/components/homepage/HomePageBusinessGrid';
import useHomePageStore, { isBusinessInBounds } from '@/store/useHomePageStore';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useStoreSynchronization } from '@/stores';
import { useTranslation } from 'react-i18next';
import { initializeApiLogging } from '@/utils/businessAppApi';
import { log } from '@/utils/logger';
import { useHomePage } from '@/components/homepage/HomePageContext';
import { useHomePageSearch } from '@/hooks/useHomePageSearch';
import { useHomePageMap } from '@/hooks/useHomePageMap';
import { ResponsiveSearchBar } from '@/components/homepage/ResponsiveSearchBar';
import { FilterPopover } from '@/components/homepage/FilterPopover';
import { SHOW_HOMEPAGE_FILTERS } from '@/components/homepage/map-layout/types';
import '@/styles/custom-scrollbar.css';

// Header (4rem) plus search strip (4.0625rem): where the search section sits once scrolled into place
const SEARCH_SECTION_TOP = 129;

// West/south, east/north. Covers the live operators, from Spain to northern Norway.
const OPENING_BOUNDS: [[number, number], [number, number]] = [[-12, 35], [35, 72]];

export function HomePage() {
  // Translation hooks
  const { t } = useTranslation('public', { keyPrefix: 'homepage' });
  
  // Store state
  const {
    businesses,
    filteredBusinesses,
    currentBounds,
    searchQuery,
    selectedBusiness,
    businessActivities,
    mapStatus,
    isLoading,
    handleBusinessSelect,
    setSearchQuery,
    activityFilters,
    selectedAnimalTypes,
    selectedAdventureTypes,
    additionalFilters,
    setActivityFilters,
    setSelectedAnimalTypes,
    setSelectedAdventureTypes,
    setSelectedSeason,
    setAdditionalFilters,
  } = useHomePageStore();

  // Local state
  const [hasSearched, setHasSearched] = useState(false);
  const mapSectionRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasOpenedRef = useRef(false);
  const [isSearchInPlace, setIsSearchInPlace] = useState(false);

  // Custom hooks for extracted functionality
  const { handleSearch, executeSearch, pendingSearchRef, pendingSearchOptionsRef, isSearchMoveRef } = useHomePageSearch(mapSectionRef, setHasSearched);
  const { handleBoundsChange, handleMapReady } = useHomePageMap(isSearchMoveRef);
  
  // Other hooks
  const { preloadCustomAdventuresForBusinesses } = useHomePage();
  const logger = useMemo(() => log.component('HomePage'), []);
  const navigate = useNavigate();

  // Show live operators on arrival, unless a search is already waiting for the map
  const showOpeningView = useCallback((mapInstance: maplibregl.Map) => {
    isSearchMoveRef.current = true;
    mapInstance.fitBounds(OPENING_BOUNDS, { padding: 24, duration: 0 });
    isSearchMoveRef.current = false;

    const b = mapInstance.getBounds();
    useHomePageStore.getState().loadBusinessesForBounds({
      northEast: { latitude: b.getNorthEast().lat, longitude: b.getNorthEast().lng },
      southWest: { latitude: b.getSouthWest().lat, longitude: b.getSouthWest().lng }
    }, true);
  }, [isSearchMoveRef]);

  // Create stable map ready handler that includes search functionality
  const handleMapReadyWithSearch = useCallback((mapInstance: maplibregl.Map) => {
    const hasPendingSearch = !!pendingSearchRef.current;
    handleMapReady(mapInstance, executeSearch, pendingSearchRef, pendingSearchOptionsRef);

    if (hasOpenedRef.current) return;
    hasOpenedRef.current = true;
    if (!hasPendingSearch) showOpeningView(mapInstance);
  }, [handleMapReady, executeSearch, showOpeningView]);

  // Handle custom adventure navigation
  const handleCustomAdventure = useCallback((businessId: string) => {
    navigate(`/custom-adventures/${businessId}`);
  }, [navigate]);

  useStoreSynchronization();

  // Initialize API logging - STABLE EFFECT
  useEffect(() => {
    initializeApiLogging(false);
  }, []);

  // The map and list only take over the wheel once the search section is in place,
  // so scrolling past the hero never gets caught halfway
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const section = mapSectionRef.current;
      if (section) setIsSearchInPlace(Math.abs(section.getBoundingClientRect().top - SEARCH_SECTION_TOP) < 8);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  const scrollToSearch = () => {
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    mapSectionRef.current?.scrollIntoView({ behavior, block: 'start' });
  };

  // Mount the map on first visit
  useEffect(() => {
    setHasSearched(true);
  }, []);

  // Preload custom adventures when businesses change - STABLE
  useEffect(() => {
    if (businesses.length > 0) {
      const businessIds = businesses.map(business => business._id).filter(Boolean) as string[];
      preloadCustomAdventuresForBusinesses(businessIds);
    }
  }, [businesses.length, preloadCustomAdventuresForBusinesses]);

  // Event handlers
  const handleDestinationClick = (query: string) => {
    setSearchQuery(query);
    handleSearch(query);
  };

  const handleClearFilters = useCallback(() => {
    setActivityFilters({
      showAnimalActivities: true,
      showAdventureActivities: true
    });
    setSelectedAnimalTypes([]);
    setSelectedAdventureTypes([]);
    setSelectedSeason(null);
    setAdditionalFilters({
      ...additionalFilters,
      groupSize: [1, 20]
    });
  }, [setActivityFilters, setSelectedAnimalTypes, setSelectedAdventureTypes, setSelectedSeason, setAdditionalFilters, additionalFilters]);

  // The map shows every operator; the list shows the ones in view
  const visibleBusinesses = useMemo(
    () => (currentBounds ? filteredBusinesses.filter(business => isBusinessInBounds(business, currentBounds)) : filteredBusinesses),
    [filteredBusinesses, currentBounds]
  );

  const hasActiveFilters =
    selectedAnimalTypes.length > 0 ||
    selectedAdventureTypes.length > 0 ||
    (!activityFilters.showAnimalActivities || !activityFilters.showAdventureActivities);
  
  // Loading state
  if (mapStatus.state === 'initializing') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary" />
          <p className="text-gray-600 font-medium">{t('map.loading.title')}</p>
          <p className="text-gray-400 text-sm">{t('map.loading.title')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header fullWidth />

      {/* Hero Section with Video – search and filters live in the sticky strip below */}
      <HeroSection onSearch={handleSearch} hideSearch hideFilters fullScreen onExplore={scrollToSearch} />

      {/* Sticky search + filters strip: follows scroll like header so filters stay available */}
      <div className="sticky z-30 top-[calc(4rem+env(safe-area-inset-top,0px))] w-full bg-white/95 backdrop-blur-sm shadow-sm border-t border-gray-200/80">
        <div className="max-w-7xl md:max-w-none mx-auto px-4 sm:px-6 md:px-4 py-2">
          <div className="flex gap-2 sm:gap-3 items-center">
            <div className="flex-1 min-w-0">
              <ResponsiveSearchBar
                compact
                value={searchQuery}
                onSearch={handleSearch}
                placeholder={t('hero.search.placeholder')}
                className="w-full bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              />
            </div>
            <p className="hidden md:block shrink-0 max-w-[16rem] truncate text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{t('businesses.sidebar.count', { count: visibleBusinesses.length })}</span>
              {searchQuery && <> · {searchQuery}</>}
            </p>
            {SHOW_HOMEPAGE_FILTERS && <div className="shrink-0 flex items-center">
              <FilterPopover
                variant="compact"
                activityFilters={activityFilters}
                selectedAnimalTypes={selectedAnimalTypes}
                selectedAdventureTypes={selectedAdventureTypes}
                selectedSeason={additionalFilters.selectedSeason}
                groupSize={additionalFilters.groupSize}
                onActivityFilterChange={setActivityFilters}
                onAnimalTypeChange={setSelectedAnimalTypes}
                onAdventureTypeChange={setSelectedAdventureTypes}
                onSeasonChange={setSelectedSeason}
                onGroupSizeChange={(size: [number, number]) => setAdditionalFilters({ ...additionalFilters, groupSize: size })}
                onClearFilters={handleClearFilters}
                showClearFilters={hasActiveFilters}
                hidePlaceboFilters={true}
              />
            </div>}
          </div>
        </div>
      </div>

      {/* Main Content with Map and List. On desktop it fills the screen below the header and search strip.
          The scroll margin adds to the 80px scroll-padding-top in index.css. */}
      <div ref={mapSectionRef} className="search-snap-target max-w-7xl md:max-w-none mx-auto px-4 sm:px-6 md:px-0 py-4 md:py-0 min-h-[50vh] sm:min-h-[600px] md:min-h-0 scroll-mt-20 md:scroll-mt-[calc(8.0625rem-80px)] overflow-x-hidden">
        <h2 className="mb-2 text-lg font-semibold text-slate-900 break-words md:hidden">
          {t('businesses.sidebar.count', { count: visibleBusinesses.length })}
          {searchQuery && <span className="font-normal text-muted-foreground"> · {searchQuery}</span>}
        </h2>

        {/* Responsive Map Layout */}
        {hasSearched ? (
          <ErrorBoundary 
            onError={(error, errorInfo) => {
              logger.error('Map component error', { error: error.message, errorInfo });
            }}
          >
            <ResponsiveMapLayout
              businesses={filteredBusinesses}
              visibleBusinesses={visibleBusinesses}
              isSearchInPlace={isSearchInPlace}
              isLoading={isLoading}
              selectedBusiness={selectedBusiness}
              onBusinessSelect={handleBusinessSelect}
              onBoundsChange={handleBoundsChange}
              onMapReady={handleMapReadyWithSearch}
              onSearch={handleSearch}
              listComponent={
                <HomePageBusinessGrid
                  filteredBusinesses={visibleBusinesses}
                  selectedBusiness={selectedBusiness}
                  businessActivities={businessActivities}
                  onBusinessSelect={handleBusinessSelect}
                  onSearch={handleSearch}
                  searchInputRef={searchInputRef}
                  onCustomAdventure={handleCustomAdventure}
                  isLoading={isLoading || mapStatus.state === 'searching'}
                />
              }
              className="mb-12 md:mb-0 md:h-[calc(100dvh-8.0625rem)] md:min-h-[520px]"
              externalMapStatus={{
                state: mapStatus.state,
                lastUpdateTime: mapStatus.lastLoadTime,
                updateMapStatus: (updates: { state?: string }) => {
                  if (updates.state) {
                    // Update map status implementation would go here
                  }
                }
              }}
            />
          </ErrorBoundary>
        ) : (
          <div
            className="mb-12 h-[55vh] min-h-[320px] sm:h-[600px] flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200"
            role="status"
            aria-live="polite"
            aria-label={pendingSearchRef.current ? "Loading map" : "Search for a destination to see the map"}
          >
            <div className="text-center max-w-md mx-auto px-6">
              {pendingSearchRef.current ? (
                <>
                  <div className="animate-spin h-12 w-12 border-4 border-primary border-r-transparent rounded-full mx-auto mb-4" aria-hidden />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {t('map.loading.preparingMap')}
                  </h3>
                  <p className="text-gray-600">
                    {t('map.loading.loadingFor', { location: pendingSearchRef.current })}
                  </p>
                </>
              ) : (
                <>
                  <div className="bg-primary/10 p-6 rounded-full mb-6 mx-auto w-fit">
                    <Search className="h-12 w-12 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{t('start.title')}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {t('start.searchPrompt')}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <h4 className="w-full text-sm text-gray-500 mb-2">{t('start.suggestions.title')}</h4>
                    {([
                      { label: t('start.suggestions.norway'), query: 'Norway' },
                      { label: t('start.suggestions.sweden'), query: 'Sweden' },
                      { label: t('start.suggestions.spain'),  query: 'Spain' },
                    ] as const).map(({ label, query }) => (
                      <button
                        key={query}
                        onClick={() => handleDestinationClick(query)}
                        className="px-4 py-2.5 min-h-[44px] bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:bg-primary hover:text-white transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 touch-manipulation"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Popular Destinations */}
      <PopularDestinations onDestinationClick={handleDestinationClick} />

      {/* Why Choose Us Section */}
      <WhyChooseUs />

      {/* Footer */}
      <Footer />
    </div>
  );
}